import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Navigation, Tag, ArrowRight, Locate, Loader2, X, Store } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { DualPrice } from './DualPrice';
import { ImageWithFallback } from './ui/image-with-fallback';

/**
 * "For You" — personalised marketplace recommendations.
 * Shows nearby discounted services the member hasn't viewed yet.
 * One-tap "Book Now" deep-links to the merchant page with item highlighted.
 */
export function ForYouRecommendations({ defaultLimit = 3 }) {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoc, setUserLoc] = useState(null);
  const [locStatus, setLocStatus] = useState('idle'); // idle | locating | granted | error

  const fmt = (n) => new Intl.NumberFormat('en-PH', {
    style: 'currency', currency: 'PHP', minimumFractionDigits: 0
  }).format(n || 0);

  // Read cached location on mount
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('aga_user_loc') || 'null');
      if (cached && typeof cached.lat === 'number' && typeof cached.lng === 'number') {
        setUserLoc(cached);
        setLocStatus('granted');
      }
    } catch (_) {/* ignore */}
  }, []);

  const fetchRecs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: defaultLimit });
      if (userLoc) {
        params.append('lat', userLoc.lat);
        params.append('lng', userLoc.lng);
      }
      const { data } = await api.get(`/api/marketplace/recommendations?${params}`);
      setItems(data.items || []);
    } catch (_) {
      setItems([]);
    }
    setLoading(false);
  }, [userLoc, defaultLimit, api]);

  useEffect(() => { fetchRecs(); }, [fetchRecs]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) { setLocStatus('error'); return; }
    setLocStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLoc(loc);
        setLocStatus('granted');
        try { localStorage.setItem('aga_user_loc', JSON.stringify(loc)); } catch (_) {/* ignore */}
      },
      () => setLocStatus('error'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, []);

  const dismiss = async (itemId) => {
    // mark viewed so it doesn't reappear, then refresh
    try { await api.post(`/api/marketplace/items/${itemId}/view`); } catch (_) {/* ignore */}
    setItems(prev => prev.filter(i => i.id !== itemId));
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-white/10 mt-6">
        <CardContent className="p-6 flex items-center justify-center min-h-[140px]">
          <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" />
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    // Soft-empty state — encourage geolocation if not yet granted
    if (!userLoc) {
      return (
        <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-white/10 mt-6" data-testid="for-you-empty">
          <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#00E5FF]/15 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-[#00E5FF]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-outfit text-base sm:text-lg font-semibold text-white mb-1">Get personalised picks</h3>
              <p className="text-[#A0B4CC] text-sm">Enable location to see exclusive deals from merchants near you.</p>
            </div>
            <Button onClick={requestLocation} disabled={locStatus === 'locating'} className="btn-primary" data-testid="for-you-enable-location">
              {locStatus === 'locating' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Locate className="w-4 h-4 mr-2" />}
              Find Nearby Deals
            </Button>
          </CardContent>
        </Card>
      );
    }
    return null;
  }

  return (
    <div className="mt-6" data-testid="for-you-section">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#00E5FF]" />
          <h2 className="font-outfit text-lg sm:text-xl font-semibold text-white">For You</h2>
          <span className="text-[10px] uppercase tracking-wider text-[#00E5FF] font-bold ml-1 px-2 py-0.5 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/20 hidden sm:inline">Personalised</span>
        </div>
        <Link to="/marketplace" className="text-[#00E5FF] hover:text-white text-xs sm:text-sm flex items-center gap-1" data-testid="for-you-see-all">
          See all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(item => (
          <Card key={item.id} className="bg-[#041A3B] border-white/10 hover:border-[#00E5FF]/40 transition-all overflow-hidden group relative" data-testid={`for-you-card-${item.id}`}>
            <button
              onClick={() => dismiss(item.id)}
              className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-[#A0B4CC] hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Not interested"
              data-testid={`for-you-dismiss-${item.id}`}
            >
              <X className="w-4 h-4" />
            </button>
            {item.image_url ? (
              <div className="h-32 sm:h-36 bg-[#000B18] overflow-hidden">
                <ImageWithFallback
                  src={item.image_url}
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  fallback={
                    <div className="h-full w-full bg-gradient-to-br from-[#041A3B] to-[#0A254D] flex items-center justify-center">
                      <Sparkles className="w-10 h-10 text-[#00E5FF]/30" />
                    </div>
                  }
                />
              </div>
            ) : (
              <div className="h-32 sm:h-36 bg-gradient-to-br from-[#041A3B] to-[#0A254D] flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-[#00E5FF]/30" />
              </div>
            )}
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-outfit text-sm sm:text-base font-semibold text-white line-clamp-2 flex-1">{item.name}</h3>
                {item.discount_percentage > 0 && (
                  <span className="bg-[#FF1744]/20 text-[#FF1744] text-[10px] font-bold rounded px-1.5 py-0.5 whitespace-nowrap" data-testid={`for-you-discount-${item.id}`}>
                    -{Math.round(item.discount_percentage)}%
                  </span>
                )}
              </div>
              <p className="text-[#5C7599] text-xs mb-2 truncate flex items-center gap-1">
                <Store className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{item.merchant_business_name || item.merchant_name}</span>
              </p>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                  {item.member_price < item.regular_price && (
                    <p className="text-[#5C7599] text-[10px] line-through">
                      <DualPrice php={item.regular_price} usdClassName="opacity-70" />
                    </p>
                  )}
                  <p className="text-[#00E676] text-base sm:text-lg font-bold font-outfit">
                    <DualPrice php={item.member_price} usdClassName="text-[10px] text-[#A0B4CC] font-medium ml-1" />
                  </p>
                </div>
                {item.distance_km != null && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20" data-testid={`for-you-distance-${item.id}`}>
                    <Navigation className="w-3 h-3" /> {fmtDistance(item.distance_km)}
                  </span>
                )}
              </div>
              <Link
                to={`/marketplace/${item.merchant_slug || item.merchant_id}#item-${item.id}`}
                onClick={() => api.post(`/api/marketplace/items/${item.id}/view`).catch(() => {})}
                data-testid={`for-you-book-${item.id}`}
              >
                <Button size="sm" className="btn-primary w-full text-xs sm:text-sm">
                  <Tag className="w-3.5 h-3.5 mr-1" />
                  {item.item_type === 'product' ? 'Purchase' : 'Book'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function fmtDistance(km) {
  if (km == null) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  if (km > 500) return 'Far';
  return `${Math.round(km)} km`;
}

export default ForYouRecommendations;
